import { Chart } from 'chart.js/auto';
import html2pdf from 'html2pdf.js';

const HEADER_HEIGHT_MM = 18;
const HEADER_BG_COLOR = [68, 116, 137]; // #447489 ($primary)
const LOGO_WIDTH_MM = 50;

let logoPngCache;

export function initPdfExport() {
  const exportPdfButton = document.getElementById('export_pdf');
  if (!exportPdfButton) return;

  exportPdfButton.onclick = _e => { generatePdf() };
}

async function generatePdf() {
  const exportPdfButton = document.getElementById('export_pdf');
  const pdfFilename = exportPdfButton.dataset.pdfFilename;
  const pdfDisclaimer = exportPdfButton.dataset.pdfDisclaimer;
  const pdfSelectedProfiles = exportPdfButton.dataset.pdfSelectedProfiles;

  let element = document.querySelector('main.container');
  const footerMarginFromBottom = 20;
  const opts = {
    pagebreak: { mode: 'css', before: '.pdf-break-before' },
    margin: [HEADER_HEIGHT_MM + 5, 0, footerMarginFromBottom + 10, 0],
    html2canvas: {
      scale: 2,
      backgroundColor: null,
      width: 1400,
      useCORS: true,
    },
    jsPDF: {
      orientation: 'portrait',
      format: [495, 371],
    },
  };

  freezeCharts();
  const logoPng = await loadLogoAsPng();
  await html2pdf()
    .set(opts)
    .from(element)
    .toContainer()
    .then(() => modifyHtmlTemplate(pdfSelectedProfiles))
    .toPdf()
    .get('pdf')
    .then(pdf => {
      addHeaderToEachPage(pdf, logoPng);
      addDisclaimerFooter(pdf, pdfDisclaimer);
    })
    .save(pdfFilename);

  element.querySelectorAll('.cloned-node').forEach(node => node.remove());
}

function freezeCharts() {
  document.querySelectorAll('canvas').forEach(canvas => {
    const chart = Chart.getChart(canvas);
    if (!chart) return;

    chart.options.animation = false;
    chart.update('none');
  });
}

function modifyHtmlTemplate(pdfSelectedProfiles) {
  const clonedDoc = $('.html2pdf__container');

  removeUnselectedTabs(clonedDoc);
  hideTooltipsForPdf(clonedDoc);
  showClonedNodes(clonedDoc);
  updateDocStyles(clonedDoc);
  addSelectedProfiles(clonedDoc, pdfSelectedProfiles);
}

function removeUnselectedTabs(doc) {
  const unselectedEngagementTab = doc.find("[id$='-engagement-tab'][aria-selected='false']");
  const unselectedFundraisingTab = doc.find("[id$='-fundraising-tab'][aria-selected='false']");
  unselectedEngagementTab.hide();
  unselectedFundraisingTab.hide();
}

function hideTooltipsForPdf(doc) {
  // FontAwesome icons render half-cut in html2canvas, and tooltips are useless in a static PDF.
  doc.find('.tool-tip').hide();
}

function addSelectedProfiles(doc, pdfSelectedProfiles) {
  const profileNamesSection = doc.find('section#selected_profile_names');
  const profileLabels = doc
    .find("label[for^='profile_id_']")
    .map((_idx, tag) => tag.innerText).get().join(", ");

  let profilesTitle = document.createElement('h2');
  profilesTitle.classList.add('text-primary', 'fs-4', 'fw-bold', 'mb-3');
  profilesTitle.innerText = pdfSelectedProfiles;

  profileNamesSection.append(profilesTitle, profileLabels);
  profileNamesSection.addClass('text-black mb-4 font-weight-bold title-font-family').css('font-size', '1.5rem');
}

function updateDocStyles(doc) {
  // Top space is reserved by opts.margin so the jsPDF header overlay sits cleanly on every page.
  doc.find('main.container').css('margin', '0 auto');
}

function addHeaderToEachPage(pdf, logoPng) {
  const totalPages = pdf.internal.getNumberOfPages();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const logoHeightMm = LOGO_WIDTH_MM * (logoPng.height / logoPng.width);

  for (let count = 1; count <= totalPages; count++) {
    pdf.setPage(count);
    pdf.setFillColor(HEADER_BG_COLOR[0], HEADER_BG_COLOR[1], HEADER_BG_COLOR[2]);
    pdf.rect(0, 0, pageWidth, HEADER_HEIGHT_MM, 'F');
    pdf.addImage(
      logoPng.dataUrl,
      'PNG',
      (pageWidth - LOGO_WIDTH_MM) / 2,
      (HEADER_HEIGHT_MM - logoHeightMm) / 2,
      LOGO_WIDTH_MM,
      logoHeightMm,
    );
  }
}

function addDisclaimerFooter(pdf, pdfDisclaimer) {
  const totalPages = pdf.internal.getNumberOfPages();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const pageWidth = pdf.internal.pageSize.getWidth();

  for (let count = 1; count <= totalPages; count++) {
    const marginFromLeft = 10;
    const marginFromBottom = pageHeight - 20;

    pdf.setPage(count);
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(10);
    pdf.setTextColor(150);
    pdf.text(
      pdfDisclaimer,
      marginFromLeft,
      marginFromBottom,
      { align: 'justify', maxWidth: pageWidth - 20 }
    );
  }
}

function showClonedNodes(doc) {
  doc.find('.cloned-node').css('visibility', 'visible');
}

async function loadLogoAsPng() {
  if (logoPngCache) return logoPngCache;

  const logoEl = document.querySelector('.logo-size');
  // Fetch the SVG and convert it to a data URL first. Loading the asset URL directly into an
  // Image with crossOrigin='anonymous' fails on Chrome when the CDN omits CORS headers; data
  // URLs are same-origin so the canvas won't be tainted when we rasterize the SVG below.
  const svgDataUrl = await fetchAsDataUrl(logoEl.src);

  const img = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = svgDataUrl;
  });

  const scale = 4;
  const width = (img.naturalWidth || logoEl.width || 224) * scale;
  const height = (img.naturalHeight || logoEl.height || 48) * scale;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(img, 0, 0, width, height);

  logoPngCache = { dataUrl: canvas.toDataURL('image/png'), width, height };
  return logoPngCache;
}

async function fetchAsDataUrl(url) {
  const response = await fetch(url, { cache: 'no-store' });
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
